/**
 * Prismribbons — Delivery Estimate Card (Variant T, interactive)
 * ---------------------------------------------------------------------------
 * States (per the design handoff): idle, loading, invalid, error.
 *   - idle: pill full opacity, "Arrives <eta>" in green, footer "Delivering
 *     to <pincode>" (or "Estimated for <city>" for an unconfirmed IP-based
 *     estimate — see below). If Delhivery reports a pincode as non-serviceable
 *     (embargo or plain), it's still treated as a confirmed pincode — those
 *     orders ship via a different courier, not blocked at checkout — the
 *     only difference is the ETA falls back to the static range instead of
 *     Delhivery's TAT (see checkPincode). There is deliberately no
 *     "unserviceable" UI state: Delhivery's response can't distinguish a
 *     pincode it just doesn't cover from one that's genuinely invalid, and
 *     showing a false rejection costs more conversions than it protects.
 *   - loading ("Loading B — bouncing dots on button", per design handoff):
 *     pill dims to 0.45 opacity, headline drops to 0.4 opacity but keeps
 *     its current text (no layout shift, no "…" swap), input becomes
 *     read-only with a dimmed bg/text, Check button keeps its shape/bg but
 *     swaps its label for three bouncing dots (aria-busy, visually-hidden
 *     "Checking…" label for screen readers). Cancel stays clickable
 *     throughout so the shopper can back out of a pending check.
 *   - invalid: fewer than 6 digits submitted — hint shown, edit row stays
 *     open. This is the only "wrong input" case the card ever surfaces —
 *     it's caught client-side, before any API call.
 *   - error: proxy/network failure on a manual Check — hint shown, edit row
 *     stays open, nothing gets committed.
 *
 * Pincode source, in order — NEVER a browser permission prompt, and the
 * pincode entry form NEVER opens by itself:
 *   1. Saved from a previous confirmed Check (localStorage) — re-validated
 *      silently on load, shown as "Delivering to <pincode>".
 *   2. Silent IP-based estimate on first visit (no saved pincode) — one
 *      background call to the proxy's `?pincode=auto`, which uses
 *      Cloudflare's own edge geolocation (no third-party API, no browser
 *      permission prompt, no client-side key). This is a rough, city-level
 *     guess, so it's used ONLY to make a positive claim: if it comes back
 *     serviceable, the footer updates to "Estimated for <city>" and the
 *     headline shows the real ETA — the form is never opened for this. If
 *     it comes back unserviceable, unavailable, or errors, none of that is
 *     shown — a guess is never used to tell someone they can't be served;
 *     the card just stays on its plain static estimate with a neutral
 *     "Check delivery time" prompt.
 *   3. Manual entry — the shopper clicks the footer button ("Check delivery
 *      time" or "Change", whichever applies) themselves, which is the only
 *      thing that ever opens the edit row.
 *
 * GET {proxyUrl}?pincode=<6-digit>       (manual, exact)
 *   -> { serviceable: false, embargo: bool }
 *   -> { serviceable: true, tat: <days>, expectedDeliveryDate: "YYYY-MM-DD" }
 *   -> { error: "..." }
 * GET {proxyUrl}?pincode=auto            (silent, approximate)
 *   -> same shape, plus { approximate: true, pincode, city } on success
 *   -> { approximate: true, unavailable: true } when no signal is available
 * See /proxy/delhivery-proxy-worker.js. The Delhivery token never reaches
 * the browser.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pr_delivery_pincode';
  var PINCODE_RE = /^[1-9][0-9]{5}$/;

  function init(root) {
    if (!root || root.__prDtInit) return;
    root.__prDtInit = true;

    var cfg = {
      proxyUrl: (root.dataset.proxyUrl || '').trim(),
      deliveringLabel: root.dataset.deliveringLabel || 'Estimated for',
      estimatedLabel: root.dataset.estimatedLabel || 'Estimated for',
      checkPromptLabel: root.dataset.checkPromptLabel || 'Check delivery time',
      changeLabel: root.dataset.changeLabel || 'Change',
      invalidHint: root.dataset.invalidHint || 'Enter a 6-digit pincode',
      errorText: root.dataset.errorText || "Couldn't check that pincode — please try again."
    };

    var els = {
      pill: root.querySelector('[data-pr-dt-pill]'),
      head: root.querySelector('[data-pr-dt-head]'),
      prefix: root.querySelector('[data-pr-dt-prefix]'),
      eta: root.querySelector('[data-pr-dt-eta]'),
      viewRow: root.querySelector('[data-pr-dt-view-row]'),
      footerLabel: root.querySelector('[data-pr-dt-footer-label]'),
      change: root.querySelector('[data-pr-dt-change]'),
      disclaimer: root.querySelector('[data-pr-dt-disclaimer]'),
      editRow: root.querySelector('[data-pr-dt-edit-row]'),
      input: root.querySelector('[data-pr-dt-input]'),
      check: root.querySelector('[data-pr-dt-check]'),
      cancel: root.querySelector('[data-pr-dt-cancel]'),
      hint: root.querySelector('[data-pr-dt-hint]')
    };

    var defaultPrefixText = els.prefix.textContent;
    var defaultEtaText = els.eta.textContent;

    // Local state.
    var pincode = null;        // exact, shopper-confirmed via Check — or null
    var approx = null;         // { pincode, city } from the silent IP lookup — or null
    var lastEta = null;        // last successful "Jun 28"-style estimate
    var editing = false;       // true ONLY after the shopper clicks the button themselves
    var checkGen = 0;          // bumped on Cancel so a late-arriving fetch response is ignored

    function render(status) {
      var loading = status === 'loading';

      els.pill.style.opacity = loading ? '0.45' : '1';
      els.head.classList.toggle('is-loading', loading); // headline dims 0.4, stays mounted — no "…" swap

      els.viewRow.style.display = editing ? 'none' : 'flex';
      els.editRow.classList.toggle('is-open', editing);
      // fine print on the shown date — hidden while editing (nothing committed yet
      // to caveat). Element is absent entirely when the merchant has toggled it
      // off in settings.
      if (els.disclaimer) els.disclaimer.hidden = editing;

      els.prefix.textContent = defaultPrefixText;
      els.eta.textContent = lastEta || defaultEtaText;

      if (pincode) {
        // A pincode Delhivery can't confirm a TAT for still ships — just via
        // a different courier — so it's shown the same as any other
        // confirmed pincode, only the ETA falls back to the static range
        // (lastEta is null in that case; see checkPincode).
        els.footerLabel.hidden = false;
        els.footerLabel.textContent = cfg.deliveringLabel + ' ' + pincode;
      } else if (approx) {
        els.footerLabel.hidden = false;
        els.footerLabel.textContent = cfg.estimatedLabel + ' ' + (approx.city || approx.pincode);
      } else {
        els.footerLabel.hidden = true;
      }

      // The button doubles as the initial prompt (nothing known yet) and the
      // "Change" affordance once something (exact or approximate) is shown.
      els.change.textContent = (pincode || approx) ? cfg.changeLabel : cfg.checkPromptLabel;

      els.check.disabled = loading;
      els.check.classList.toggle('is-loading', loading);
      els.check.setAttribute('aria-busy', loading ? 'true' : 'false');
      // Dots are decorative (aria-hidden) — give screen readers "Checking…"
      // as the button's accessible name while loading, "Check" otherwise.
      if (loading) els.check.setAttribute('aria-label', 'Checking…');
      else els.check.removeAttribute('aria-label');
      els.input.disabled = loading;

      var showHint = status === 'invalid' || status === 'error';
      els.hint.classList.toggle('is-visible', showHint);
      if (status === 'invalid') els.hint.textContent = cfg.invalidHint;
      else if (status === 'error') els.hint.textContent = cfg.errorText;
      else els.hint.textContent = '';
    }

    function formatEstimate(dateStr) {
      // dateStr is "YYYY-MM-DD" from Delhivery; render like the static
      // fallback's "Jun 28" (single date — TAT gives one point estimate).
      var parts = (dateStr || '').split('-');
      if (parts.length !== 3) return dateStr || '';
      var d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Manual, exact-pincode Check (and silent re-validation of a saved
    // pincode on load). Commits on success, surfaces loading/error states.
    // Delhivery non-serviceability (embargo or plain) does NOT mean the
    // order can't ship — Prismribbons routes those through other couriers —
    // so it's treated the same as a confirmed pincode, just without a
    // Delhivery-derived ETA (falls back to the static range instead).
    function checkPincode(candidate, silent) {
      if (!cfg.proxyUrl) {
        if (!silent) render('error');
        return;
      }
      if (!silent) render('loading');

      var myGen = checkGen;
      var sep = cfg.proxyUrl.indexOf('?') > -1 ? '&' : '?';
      fetch(cfg.proxyUrl + sep + 'pincode=' + encodeURIComponent(candidate))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (myGen !== checkGen) return; // shopper cancelled — discard this response
          if (!data || data.error) {
            if (!silent) render('error');
            return;
          }
          pincode = candidate;
          approx = null;
          editing = false;
          try { localStorage.setItem(STORAGE_KEY, pincode); } catch (e) {}
          lastEta = data.serviceable ? formatEstimate(data.expectedDeliveryDate) : null;
          render('idle');
        })
        .catch(function () {
          if (myGen !== checkGen) return; // shopper cancelled — discard this response
          if (!silent) render('error');
        });
    }

    // Silent IP-based estimate — positive-only. A negative or missing result
    // just leaves the card exactly as it was (never opens the form, never
    // shows an error, never claims "unserviceable" from a guess).
    function tryApproxLookup() {
      if (!cfg.proxyUrl) return;
      var sep = cfg.proxyUrl.indexOf('?') > -1 ? '&' : '?';
      fetch(cfg.proxyUrl + sep + 'pincode=auto')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (pincode || editing) return; // shopper already acted — don't override
          if (data && data.serviceable && data.pincode) {
            approx = { pincode: data.pincode, city: data.city || null };
            lastEta = formatEstimate(data.expectedDeliveryDate);
            render('idle');
          }
        })
        .catch(function () {});
    }

    els.change.addEventListener('click', function () {
      editing = true;
      els.input.value = pincode || (approx && approx.pincode) || '';
      render('idle');
      els.input.focus();
    });

    els.cancel.addEventListener('click', function () {
      checkGen++; // invalidate any in-flight manual check so its response is discarded
      editing = false;
      render('idle');
    });

    els.input.addEventListener('input', function (e) {
      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6);
    });
    els.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') els.check.click();
    });

    els.check.addEventListener('click', function () {
      var candidate = els.input.value || '';
      if (!PINCODE_RE.test(candidate)) {
        render('invalid');
        return;
      }
      checkPincode(candidate, false);
    });

    var savedPincode = null;
    try { savedPincode = localStorage.getItem(STORAGE_KEY); } catch (e) {}

    render('idle'); // paint the default state immediately — form stays closed

    if (savedPincode && PINCODE_RE.test(savedPincode)) {
      checkPincode(savedPincode, true);
    } else {
      tryApproxLookup();
    }
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-pr-delivery-estimate]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var node = e.target.querySelector && e.target.querySelector('[data-pr-delivery-estimate]');
    if (node) init(node);
  });
})();
